import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { siteData } from "@/store/siteData";
import { apiGetMessages, ApiMessage } from "@/utils/apiClient";
import type { Tab } from "./AdminSidebar";

const msgStatusColors: Record<string, string> = {
  new: "bg-[#3B82F6]/20 text-[#B8C5D8] border border-[#3B82F6]/30",
  read: "bg-white/10 text-[#7B8FAF] border border-white/10",
  replied: "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30",
};
const msgStatusLabel: Record<string, string> = { new: "Yeni", read: "Okundu", replied: "Yanıtlandı" };

export default function DashboardOverview({ onNavigate }: { onNavigate?: (t: Tab) => void }) {
  const navigate = useNavigate();
  const products = siteData.getProducts();
  const featured = products.filter((p) => p.featured).length;
  const partners = siteData.getPartners();
  const solutions = siteData.getSolutions().items.length;
  const settings = siteData.getGeneralSettings();

  const [msgs, setMsgs] = useState<ApiMessage[]>([]);
  const [msgLoading, setMsgLoading] = useState(true);

  const loadMessages = useCallback(async () => {
    setMsgLoading(true);
    try { setMsgs(await apiGetMessages()); } catch { /* sessizce */ } finally { setMsgLoading(false); }
  }, []);

  useEffect(() => {
    loadMessages();
    const interval = setInterval(loadMessages, 30_000);
    return () => clearInterval(interval);
  }, [loadMessages]);

  const newMessages = msgs.filter((m) => m.status === "new").length;

  const stats = [
    { label: "Toplam Ürün", value: products.length.toString(), icon: "ri-computer-line", change: `${featured} öne çıkan`, tab: "products" as Tab },
    { label: "Çözüm", value: solutions.toString(), icon: "ri-apps-2-line", change: "Ana sayfada", tab: "homepage" as Tab },
    { label: "Stratejik Ortak", value: partners.length.toString(), icon: "ri-links-line", change: "Aktif ortaklıklar", tab: "partners" as Tab },
    { label: "Yeni Mesaj", value: msgLoading ? "…" : newMessages.toString(), icon: "ri-mail-open-line", change: "Yanıt bekliyor", highlight: newMessages > 0, tab: "messages" as Tab },
  ];

  const quickLinks: { label: string; desc: string; icon: string; tab: Tab }[] = [
    { label: "Ana Sayfa İçerikleri", desc: "Hero, çözümler, ürünler, makaleler…", icon: "ri-home-4-line", tab: "homepage" },
    { label: "Ürünlerimiz", desc: "Ürün ekle, düzenle, öne çıkar", icon: "ri-computer-line", tab: "products" },
    { label: "Site Ayarları", desc: "İletişim, logo, sosyal medya", icon: "ri-settings-3-line", tab: "settings" },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-white tracking-tight">Genel Bakış</h1>
        <p className="text-[#7B8FAF] text-sm mt-1">{settings.companyName} — Site Yönetim Özeti</p>
      </div>

      {/* Görsel Editör vurgusu */}
      <button
        onClick={() => navigate("/site-inspector")}
        className="w-full flex items-center gap-4 rounded-xl p-5 border border-emerald-500/30 bg-gradient-to-r from-emerald-500/15 to-[#3B82F6]/10 hover:from-emerald-500/25 transition-all cursor-pointer text-left"
      >
        <div className="w-11 h-11 grid place-items-center rounded-lg bg-emerald-500/20 flex-shrink-0">
          <i className="ri-edit-box-line text-emerald-300 text-xl"></i>
        </div>
        <div className="flex-1">
          <p className="text-white font-semibold">Görsel Editör ile düzenle</p>
          <p className="text-[#A5B5CC] text-sm">Siteyi canlı önizlemede görüp bölümlere tıklayarak düzenleyin.</p>
        </div>
        <i className="ri-arrow-right-line text-emerald-300 text-xl"></i>
      </button>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <button
            key={stat.label}
            onClick={() => onNavigate?.(stat.tab)}
            className={`text-left rounded-xl p-5 border cursor-pointer transition-all hover:border-[#3B82F6]/60 ${stat.highlight ? "bg-[#3B82F6]/10 border-[#3B82F6]/40" : "bg-[#141E35] border-[#2B3654]"}`}
          >
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#3B82F6]/20 mb-3">
              <i className={`${stat.icon} text-[#B8C5D8] text-lg`}></i>
            </div>
            <p className="text-2xl font-black text-white">{stat.value}</p>
            <p className="text-[#7B8FAF] text-xs mt-1">{stat.label}</p>
            <p className={`text-xs mt-1 ${stat.highlight ? "text-[#B8C5D8]" : "text-[#5E7398]"}`}>{stat.change}</p>
          </button>
        ))}
      </div>

      {/* Hızlı Erişim + Ortaklar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-6">
          <h2 className="text-white font-semibold mb-5 flex items-center gap-2"><i className="ri-flashlight-line text-[#B8C5D8]"></i> Hızlı Erişim</h2>
          <div className="space-y-2">
            {quickLinks.map((q) => (
              <button key={q.tab} onClick={() => onNavigate?.(q.tab)} className="w-full flex items-center gap-3 p-3 rounded-lg border border-[#2B3654] hover:border-[#3B82F6]/50 hover:bg-white/5 transition-all cursor-pointer text-left">
                <div className="w-9 h-9 grid place-items-center rounded-lg bg-[#3B82F6]/15 flex-shrink-0"><i className={`${q.icon} text-[#B8C5D8]`}></i></div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium">{q.label}</p>
                  <p className="text-[#7B8FAF] text-xs truncate">{q.desc}</p>
                </div>
                <i className="ri-arrow-right-s-line text-[#5E7398]"></i>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-6">
          <h2 className="text-white font-semibold mb-5 flex items-center gap-2"><i className="ri-team-line text-[#B8C5D8]"></i> Stratejik Ortaklar</h2>
          <div className="space-y-3 max-h-72 overflow-y-auto">
            {partners.map((partner) => (
              <div key={partner.id} className="flex items-center justify-between py-2 border-b border-[#1E2B4A] last:border-0">
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{partner.name}</p>
                  <p className="text-[#7B8FAF] text-xs truncate">{partner.sector}</p>
                </div>
                <span className="text-[#5E7398] text-xs ml-3 flex-shrink-0">{partner.since}&rsquo;den beri</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Son Mesajlar */}
      <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-white font-semibold flex items-center gap-2">
            <i className="ri-mail-line text-[#B8C5D8]"></i> Son Mesajlar
            {newMessages > 0 && <span className="ml-2 bg-[#3B82F6] text-white text-xs px-2 py-0.5 rounded-full">{newMessages} yeni</span>}
          </h2>
          <button onClick={loadMessages} disabled={msgLoading} className="w-8 h-8 flex items-center justify-center text-[#7B8FAF] hover:text-white border border-[#2B3654] rounded-lg cursor-pointer hover:border-[#3B82F6] transition-all disabled:opacity-40" title="Yenile">
            <i className={`ri-refresh-line text-sm ${msgLoading ? "animate-spin" : ""}`}></i>
          </button>
        </div>
        {msgLoading && msgs.length === 0 ? (
          <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-2xl text-[#3B82F6]"></i></div>
        ) : msgs.length === 0 ? (
          <div className="py-12 text-center"><i className="ri-inbox-line text-4xl text-[#5E7398]"></i><p className="text-[#7B8FAF] text-sm mt-3">Henüz mesaj yok</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#2B3654]">
                  <th className="text-left text-[#5E7398] font-medium pb-3 pr-4 text-xs uppercase tracking-widest">Gönderen</th>
                  <th className="text-left text-[#5E7398] font-medium pb-3 pr-4 text-xs uppercase tracking-widest hidden md:table-cell">Konu</th>
                  <th className="text-left text-[#5E7398] font-medium pb-3 text-xs uppercase tracking-widest">Durum</th>
                </tr>
              </thead>
              <tbody>
                {msgs.slice(0, 6).map((msg) => (
                  <tr key={msg.id} className="border-b border-[#1E2B4A] last:border-0">
                    <td className="py-3 pr-4"><p className="text-white font-medium">{msg.name}</p><p className="text-[#7B8FAF] text-xs">{msg.date}</p></td>
                    <td className="py-3 pr-4 hidden md:table-cell"><p className="text-[#A5B5CC] truncate max-w-[260px]">{msg.subject}</p></td>
                    <td className="py-3"><span className={`text-xs px-2.5 py-1 rounded-full font-medium ${msgStatusColors[msg.status] || msgStatusColors.read}`}>{msgStatusLabel[msg.status] || msg.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
